/**
 * Modern Availability Calendar Component
 * Handles calendar rendering and user interactions for listing availability management
 */
class AvailabilityCalendar {
    constructor(config = {}) {
        // Configuration
        this.listingId = config.listingId || null;
        this.apiEndpoint = config.apiEndpoint || '/api/listings/' + this.listingId + '/calendar';
        this.initialBookings = config.bookings || {};
        this.readOnly = config.readOnly !== false; // Default to read-only
          // Calendar state
        this.currentDate = new Date();
        this.bookings = { ...this.initialBookings };
        this.calendarData = {};
        this.isLoading = false;
        
        // Selected date range for user bookings (uses spanning bars)
        this.selectedDates = {
            startDate: null,
            endDate: null,
            dates: [] // Array of selected date strings
        };
        
        // DOM elements
        this.calendarGrid = null;
        this.currentMonthElement = null;
        this.prevMonthBtn = null;
        this.nextMonthBtn = null;
          // Initialize
        this.init();
    }    async init() {
        // Find DOM elements
        this.calendarGrid = document.getElementById('calendar-grid');
        this.currentMonthElement = document.getElementById('current-month');
        this.prevMonthBtn = document.getElementById('prev-month');
        this.nextMonthBtn = document.getElementById('next-month');
        
        if (!this.calendarGrid) {
            console.error('Calendar grid element not found');
            return;
        }
        
        this.bindEvents();
        
        // Load calendar data from API if listing ID is provided
        if (this.listingId && this.readOnly) {
            await this.loadCalendarData();
        }
        
        this.renderCalendar();
    }

    async loadCalendarData() {
        if (this.isLoading) return;
        
        this.isLoading = true;
        
        try {
            const response = await fetch(this.apiEndpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.success) {
                this.calendarData = result.calendar_data;
                console.log('Calendar data loaded successfully', result.metadata);
            } else {
                console.error('Failed to load calendar data:', result.message);
            }
        } catch (error) {
            console.error('Error loading calendar data:', error);
        } finally {
            this.isLoading = false;
        }
    }

    bindEvents() {
        if (this.prevMonthBtn) {
            this.prevMonthBtn.addEventListener('click', () => {
                this.currentDate.setMonth(this.currentDate.getMonth() - 1);
                this.renderCalendar();
            });
        }
        
        if (this.nextMonthBtn) {
            this.nextMonthBtn.addEventListener('click', () => {
                this.currentDate.setMonth(this.currentDate.getMonth() + 1);
                this.renderCalendar();
            });
        }
    }

    formatDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }    renderCalendar() {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        
        // Update month display
        if (this.currentMonthElement) {
            this.currentMonthElement.textContent = new Intl.DateTimeFormat('en-US', {
                month: 'long',
                year: 'numeric'
            }).format(this.currentDate);
        }
        
        // Clear calendar grid (including loading state)
        this.calendarGrid.innerHTML = '';
        
        // Get first day of month and number of days
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const daysInMonth = lastDayOfMonth.getDate();
        
        // Get first day of week (Monday = 0, Sunday = 6)
        let firstDayOfWeek = firstDayOfMonth.getDay();
        firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
        
        // Add previous month's trailing days
        const prevMonth = new Date(year, month - 1, 0);
        for (let i = firstDayOfWeek - 1; i >= 0; i--) {
            const day = prevMonth.getDate() - i;
            const dayElement = this.createDayElement(day, 'other-month');
            this.calendarGrid.appendChild(dayElement);
        }
          // Add current month's days
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to compare dates only
        
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateString = this.formatDate(date);
            const isToday = dateString === this.formatDate(today);
            const isPast = date < today;
            
            let classes = [];
            
            // Check if date is in the past
            if (isPast) {
                classes.push('past');
            } else if (isToday) {
                classes.push('today');
            }
              // Check for availability status from API data or initial bookings
            const dayData = this.calendarData[dateString] || null;
            let availabilityStatus = 'available'; // Default to available
            
            if (dayData && dayData.status) {
                availabilityStatus = dayData.status;
            } else if (this.bookings[dateString]) {
                availabilityStatus = this.bookings[dateString];
            }
              // Add availability status class for background shading
            classes.push(availabilityStatus);
            
            // Add booking span classes for bars (booked, pending, blocked)
            if (availabilityStatus !== 'available') {
                const bookingSpanClass = this.getBookingSpanClass(dateString, availabilityStatus);
                if (bookingSpanClass) {
                    classes.push('has-booking');
                    classes.push(bookingSpanClass);
                }
            }
            
            // Check if this date is in selected range (for booking requests)
            const selectedSpanClass = this.getSelectedSpanClass(dateString);
            if (selectedSpanClass) {
                classes.push(selectedSpanClass);
                classes.push(`selected-${availabilityStatus}`); // For color-coded selection bars
            }
            
            const dayElement = this.createDayElement(day, classes.join(' '), dayData);
            dayElement.setAttribute('data-date', dateString);
            dayElement.setAttribute('data-availability-status', availabilityStatus);
            this.calendarGrid.appendChild(dayElement);
        }
        
        // Add next month's leading days
        const totalCells = this.calendarGrid.children.length;
        const remainingCells = 42 - totalCells; // 6 weeks * 7 days
        for (let day = 1; day <= remainingCells; day++) {
            const dayElement = this.createDayElement(day, 'other-month');
            this.calendarGrid.appendChild(dayElement);        }
    }

    /**
     * Create a day element with appropriate styling
     * This method handles both availability status (background shading) 
     * and selected dates (spanning bars for user selections)
     */

    createDayElement(day, className, dayData = null) {
        const dayElement = document.createElement('div');
        dayElement.className = `calendar-day ${className}`;
        dayElement.textContent = day;
        
        // Add tooltip if there's day data
        if (dayData && dayData.tooltip) {
            dayElement.title = dayData.tooltip;
            dayElement.setAttribute('data-toggle', 'tooltip');
        }
          // Add click handler for availability management (if not read-only and not past dates)
        if (!this.readOnly && !className.includes('other-month') && !className.includes('past')) {
            dayElement.addEventListener('click', (e) => {
                const date = e.target.getAttribute('data-date');
                if (date) {
                    this.handleDayClick(date, e.target);
                }
            });
            dayElement.style.cursor = 'pointer';
        }
        
        return dayElement;
    }    handleDayClick(date, element) {
        // Cycle through availability states: available -> pending -> unavailable -> available
        const currentState = this.bookings[date] || 'available';
        const states = ['available', 'pending', 'unavailable'];
        const currentIndex = states.indexOf(currentState);
        const nextIndex = (currentIndex + 1) % states.length;
        const nextState = states[nextIndex];
        
        // Update local state and element styling
        if (nextState === 'available') {
            delete this.bookings[date];
        } else {
            this.bookings[date] = nextState;
        }
        
        // Update element classes (remove old availability status and add new one)
        element.className = element.className.replace(/\b(available|pending|unavailable)\b/g, '');
        element.className += ` ${nextState}`;
        element.setAttribute('data-availability-status', nextState);
        
        // Send update to backend
        this.updateAvailability(date, nextState);
        
        console.log(`Date ${date} changed to ${nextState}`);
    }

    async updateAvailability(date, status) {
        if (!this.listingId) {
            console.warn('No listing ID provided for availability update');
            return;
        }

        try {
            const response = await fetch(this.apiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                },
                body: JSON.stringify({
                    listing_id: this.listingId,
                    date: date,
                    status: status
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            
            if (result.success) {
                console.log('Availability updated successfully');
                // Optionally trigger an event for other components to listen to
                this.triggerEvent('availability-updated', { date, status });
            } else {
                console.error('Failed to update availability:', result.message);
                // Revert the change if the server update failed
                this.revertChange(date);
            }
        } catch (error) {
            console.error('Error updating availability:', error);
            // Revert the change if there was an error
            this.revertChange(date);
        }
    }    revertChange(date) {
        // Find the element and revert to previous state
        const element = document.querySelector(`[data-date="${date}"]`);
        if (element) {
            const previousState = this.bookings[date] || 'available';
            // Remove current availability status and add previous one
            element.className = element.className.replace(/\b(available|pending|unavailable)\b/g, '');
            element.className += ` ${previousState}`;
            element.setAttribute('data-availability-status', previousState);
        }
    }

    triggerEvent(eventName, data) {
        const event = new CustomEvent(eventName, { 
            detail: data,
            bubbles: true
        });
        document.dispatchEvent(event);
    }

    // Public methods for external control
    updateBookings(newBookings) {
        this.bookings = { ...newBookings };
        this.renderCalendar();
    }

    setReadOnly(readOnly) {
        this.readOnly = readOnly;
        // Re-render to update click handlers
        this.renderCalendar();
    }

    goToDate(date) {
        this.currentDate = new Date(date);
        this.renderCalendar();
    }    getCurrentBookings() {
        return { ...this.bookings };
    }

    /**
     * Handle date selection for booking requests
     * This creates spanning bars across selected dates
     */
    selectDateRange(startDate, endDate) {
        this.selectedDates.startDate = startDate;
        this.selectedDates.endDate = endDate;
        this.selectedDates.dates = this.getDatesBetween(startDate, endDate);
        this.renderCalendar(); // Re-render to show selected dates
    }

    /**
     * Clear selected date range
     */
    clearSelectedDates() {
        this.selectedDates.startDate = null;
        this.selectedDates.endDate = null;
        this.selectedDates.dates = [];
        this.renderCalendar(); // Re-render to remove selection
    }

    /**
     * Get array of date strings between start and end dates (inclusive)
     */
    getDatesBetween(startDate, endDate) {
        const dates = [];
        const currentDate = new Date(startDate);
        const end = new Date(endDate);
        
        while (currentDate <= end) {
            dates.push(this.formatDate(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
        return dates;
    }

    /**
     * Get selection span class for a date
     */
    getSelectedSpanClass(dateString) {
        if (!this.selectedDates.dates.includes(dateString)) {
            return null;
        }
        
        const index = this.selectedDates.dates.indexOf(dateString);
        const totalDates = this.selectedDates.dates.length;
        
        if (totalDates === 1) {
            return 'selected-single';
        } else if (index === 0) {
            return 'selected-start';
        } else if (index === totalDates - 1) {
            return 'selected-end';
        } else {
            return 'selected-middle';
        }
    }
    
    /**
     * Get booking span class for a date to create booking bars
     * This analyzes consecutive dates with the same booking status
     */
    getBookingSpanClass(dateString, status) {
        // Get all dates in current calendar view with the same booking_id
        const currentBookingId = this.calendarData[dateString]?.booking_id;
        
        if (!currentBookingId && !['blocked'].includes(status)) {
            return null; // Only process actual bookings and blocked periods
        }
        
        // Find all consecutive dates with same status/booking
        const spanDates = this.getBookingSpanDates(dateString, status, currentBookingId);
        
        if (spanDates.length <= 1) {
            return status === 'blocked' ? 'booking-single' : null; // Single day
        }
        
        const index = spanDates.indexOf(dateString);
        
        if (index === 0) {
            return 'booking-start';
        } else if (index === spanDates.length - 1) {
            return 'booking-end';
        } else {
            return 'booking-middle';
        }
    }
    
    /**
     * Get array of consecutive dates with same booking status/ID
     */    getBookingSpanDates(dateString, status, bookingId) {
        const dates = [];
        const startDate = new Date(dateString);
        
        // Go backwards to find start of span (excluding the current date)
        let current = new Date(startDate);
        current.setDate(current.getDate() - 1);
        while (this.isDatePartOfSameBooking(this.formatDate(current), status, bookingId)) {
            dates.unshift(this.formatDate(current));
            current.setDate(current.getDate() - 1);
        }
        
        // Add the current date
        dates.push(dateString);
        
        // Go forwards to find end of span (excluding the current date)
        current = new Date(startDate);
        current.setDate(current.getDate() + 1);
        while (this.isDatePartOfSameBooking(this.formatDate(current), status, bookingId)) {
            dates.push(this.formatDate(current));
            current.setDate(current.getDate() + 1);
        }
        
        return dates;
    }
    
    /**
     * Check if a date is part of the same booking span
     */
    isDatePartOfSameBooking(dateString, status, bookingId) {
        const dayData = this.calendarData[dateString];
        
        if (!dayData || dayData.status !== status) {
            return false;
        }
        
        // For blocked periods, just check status match
        if (status === 'blocked') {
            return true;
        }
        
        // For bookings, check booking_id match
        return dayData.booking_id === bookingId;
    }
}

// Auto-initialize if calendar wrapper exists
document.addEventListener('DOMContentLoaded', function() {
    const calendarWrapper = document.querySelector('.availability-calendar-wrapper');
    if (calendarWrapper) {
        // Try to get configuration from data attributes or window object
        const config = {
            listingId: calendarWrapper.dataset.listingId || window.listingId,
            apiEndpoint: calendarWrapper.dataset.apiEndpoint || '/api/availability',
            readOnly: calendarWrapper.dataset.readOnly === 'true',
            bookings: window.calendarBookings || {}
        };
        
        // Initialize calendar
        window.availabilityCalendar = new AvailabilityCalendar(config);
        
        console.log('Availability Calendar initialized');
    }
});
            
            // End of file
